import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	NewRiskProfileSchemaStep0,
	NewRiskExpositionAreaSchemaStep0,
	NewRiskSchemaStep1,
} from './ProfileRisk.schema';
import { formatErrorResponse, validate } from 'utils/zodValidation';
import {
	getJobProfile,
	getRisks,
	postJobProfile,
	putJobProfile,
} from './services';
import { FormErrorType, JobRiskType, RiskType } from './types/types';
import { postFormatter, putFormmater } from './formatter/formatter';
import { JobProfileContext } from 'Views/JobProfile/JobProfile.context';
import { JobProfileType } from 'Views/JobProfile/JobProfile.enums';
import Analytics from 'helpers/analytics';
import { useToast } from '@welbe/web-design-system';

export const useNewRisk = () => {
	const { toast } = useToast();
	const navigate = useNavigate();
	const { id, jobProfileType } = useContext(JobProfileContext);
	const [step, setStep] = useState(0);
	const [risk, setRisk] = useState<RiskType>({
		name: '',
		description: '',
		address: '',
		associatedRisk: [],
	});
	const [formError, setFormError] = useState<FormErrorType>();
	const [jobRisks, setJobRisks] = useState<JobRiskType[]>([]);
	const [hasError, setHasError] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	const preventReRenderJobProfile = useRef(false);
	const preventReRenderRisks = useRef(false);

	const renderTitle = () => {
		if (step === 0)
			return jobProfileType === JobProfileType.RISK_PROFILE
				? !id
					? 'Nuevo perfil de riesgo'
					: 'Editar perfil de riesgo'
				: !id
				? 'Nueva área de exposición'
				: 'Editar área de exposición';
		if (step === 1)
			return jobProfileType === JobProfileType.RISK_PROFILE
				? 'Riesgos asociados a este perfil de riesgos'
				: 'Riesgos asociados a esta área de exposición';
	};

	const handlePut = async () => {
		try {
			setIsLoading(true);
			await putJobProfile(id, putFormmater(jobProfileType, risk));
			navigate(`/job-profile/summary/${jobProfileType}/${id}`);
			return;
		} catch (error) {
			toast.error('Hubo un error al cambiar el riesgo.');
		} finally {
			setIsLoading(false);
		}
	};

	const handlePost = async () => {
		try {
			setIsLoading(true);
			const { data } = await postJobProfile(
				postFormatter(jobProfileType, risk),
			);
			if (data?.content?.id) {
				navigate(
					`/job-profile/risk-factors/${jobProfileType}/${data?.content?.id}`,
				);
				return;
			}
			toast.error('Hubo un error al crear el lo riesgo.');
		} catch (error) {
			toast.error('Hubo un error al crear el lo riesgo.');
		} finally {
			setIsLoading(false);
		}
	};

	const nextStep = async () => {
		try {
			await validate(
				step === 0
					? jobProfileType === JobProfileType.RISK_PROFILE
						? NewRiskProfileSchemaStep0
						: NewRiskExpositionAreaSchemaStep0
					: NewRiskSchemaStep1,
				risk,
			);
			if (id && step === 0) {
				await handlePut();
				return;
			}
			if (step === 1) {
				Analytics.track(
					'job-profile',
					`${
						jobProfileType === JobProfileType.RISK_PROFILE
							? '(Risk Profile)'
							: '(Exposure Area)'
					} Associated Risks selection screen`,
				);
				await handlePost();
				return;
			}
			Analytics.track(
				'job-profile',
				jobProfileType === JobProfileType.RISK_PROFILE
					? '(Risk Profile) Name/Description Filled'
					: '(Exposure Area) Name/Description/Location Filled',
			);
			setStep(step + 1);
		} catch (error) {
			if (error.zodError) {
				setFormError({ ...formatErrorResponse(error.zodError) });
			}
		}
	};

	const prevStep = () => {
		if (id && step === 0) {
			navigate(`/job-profile/summary/${jobProfileType}/${id}`);
			return;
		}
		if (step === 0) {
			navigate('/job-profile');
			return;
		}
		setStep(step - 1);
	};

	const handleChangeRisk = (field: string, value: string) => {
		if (field === 'associatedRisk') {
			setFormError((prev) => ({ ...prev, associatedRisk: '' }));
			if (risk?.associatedRisk?.includes(value)) {
				setRisk(
					(prev) =>
						({
							...prev,
							associatedRisk: prev.associatedRisk.filter(
								(item) => item !== value,
							),
						} as RiskType),
				);

				Analytics.track(
					'job-profile',
					`${
						jobProfileType === JobProfileType.RISK_PROFILE
							? '(Risk Profile) '
							: '(Exposure Area) '
					}${jobRisks
						.find((risk) => risk.id === value)
						?.name.trim()} Risk unselected)`,
				);
				return;
			}

			Analytics.track(
				'job-profile',
				`${
					jobProfileType === JobProfileType.RISK_PROFILE
						? '(Risk Profile) '
						: '(Exposure Area) '
				}${jobRisks
					.find((risk) => risk.id === value)
					?.name.trim()} Risk selected)`,
			);
			setRisk(
				(prev) =>
					({
						...prev,
						associatedRisk: [...prev.associatedRisk, value],
					} as RiskType),
			);
			return;
		}
		setFormError((prev) => ({ ...prev, [field]: '' }));
		setRisk((prev) => ({ ...prev, [field]: value }));
	};

	const fetchRisks = async () => {
		try {
			setIsLoading(true);
			const { data } = await getRisks();
			setJobRisks(data?.content?.jobRisks);
		} catch (error) {
			setHasError(true);
			console.error('Error fetching risks', error);
		} finally {
			setIsLoading(false);
		}
	};

	const fetchJobProfile = async () => {
		try {
			setIsLoading(true);
			const { data } = await getJobProfile(id);
			setRisk({
				...data?.content,
				associatedRisk: data?.content?.risks,
			});
		} catch (error) {
			setHasError(true);
			console.error('Error fetching job profile', error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (!preventReRenderJobProfile.current) {
			if (id) {
				fetchJobProfile();
			}
			preventReRenderJobProfile.current = true;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	useEffect(() => {
		if (!preventReRenderRisks.current) {
			fetchRisks();
			preventReRenderRisks.current = true;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		id,
		jobProfileType,
		jobRisks,
		hasError,
		step,
		risk,
		formError,
		isLoading,
		renderTitle,
		nextStep,
		prevStep,
		handleChangeRisk,
	};
};
